import React, { useState, useMemo, useEffect } from 'react';
import fetchData from "../api/fetchApi";
import { Calendar, ArrowUpDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Table from './common/Table';

const DonutChart = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let cumulative = 0;

  const segments = data.map(item => {
    const percentage = item.value / total;
    const startAngle = (cumulative / total) * 360;
    cumulative += item.value;
    const endAngle = (cumulative / total) * 360;
    return { ...item, percentage, startAngle, endAngle };
  });

  const getArcPath = (startAngle, endAngle, innerRadius, outerRadius) => {
    const start = polarToCartesian(outerRadius, endAngle);
    const end = polarToCartesian(outerRadius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    const d = [
      "M", start.x, start.y,
      "A", outerRadius, outerRadius, 0, largeArcFlag, 0, end.x, end.y,
      "L", polarToCartesian(innerRadius, startAngle).x, polarToCartesian(innerRadius, startAngle).y,
      "A", innerRadius, innerRadius, 0, largeArcFlag, 1, polarToCartesian(innerRadius, endAngle).x, polarToCartesian(innerRadius, endAngle).y,
      "Z"
    ].join(" ");
    return d;
  };

  const polarToCartesian = (radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: 100 + (radius * Math.cos(angleInRadians)),
      y: 100 + (radius * Math.sin(angleInRadians))
    };
  };

  return (
    <div className="flex items-center gap-8">
      <svg viewBox="0 0 200 200" className="w-40 h-40">
        {segments.map((segment, i) => (
          <path key={i} d={getArcPath(segment.startAngle, segment.endAngle, 60, 80)} fill={segment.color} />
        ))}
      </svg>
      <div className="space-y-3">
        <h3 className="font-semibold text-lg text-[#1D2035]">إجمالي المقالات</h3>
        {data.map((item, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></span>
              <span>{item.label}</span>
            </div>
            <div className="font-semibold text-[#1D2035]">{item.value.toLocaleString()}</div>
            <div className="text-gray-500">{(item.percentage * 100).toFixed(0)}%</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const LineChart = ({ data }) => {
    const [hoveredPoint, setHoveredPoint] = useState(null);
    const chartWidth = 500;
    const chartHeight = 150;
    const padding = 20;
    const maxValue = Math.max(...data.map(p => p.value));
  
    const points = data.map((point, i) => {
      const x = (i / (data.length - 1)) * (chartWidth - 2 * padding) + padding;
      const y = chartHeight - padding - (point.value / maxValue) * (chartHeight - 2 * padding);
      return { x, y, ...point };
    });
  
    const linePath = points.map((p, i) => (i === 0 ? 'M' : 'L') + `${p.x} ${p.y}`).join(' ');
  
    const handleMouseMove = (e) => {
      const svgRect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - svgRect.left;
      const closestPoint = points.reduce((prev, curr) => 
        Math.abs(curr.x - x) < Math.abs(prev.x - x) ? curr : prev
      );
      setHoveredPoint(closestPoint);
    };
  
    return (
      <div className="relative">
        <div className="flex justify-between items-start mb-4">
            <div>
                <p className="text-sm text-gray-500">إجمالي الزيارات</p>
                <p className="text-3xl font-bold text-[#1D2035]">48,294</p>
            </div>
            <p className="text-sm text-green-600">+8% مقارنة بالأسبوع الماضي</p>
        </div>
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} onMouseMove={handleMouseMove} onMouseLeave={() => setHoveredPoint(null)}>
          {/* Grid lines */}
          {[0, 2500, 5000, 7500, 10000].map(val => {
            const y = chartHeight - padding - (val / maxValue) * (chartHeight - 2 * padding);
            return (
              <g key={val}>
                <line x1={padding} y1={y} x2={chartWidth - padding} y2={y} stroke="#e5e7eb" strokeDasharray="4 4" />
                <text x={chartWidth - padding + 5} y={y + 4} fontSize="10" fill="#9ca3af">{val/1000}K</text>
              </g>
            )
          })}
          <path d={linePath} fill="none" stroke="#c1122b" strokeWidth="2" />
          {points.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="3" fill="#c1122b" />)}
          {hoveredPoint && (
            <g>
              <line x1={hoveredPoint.x} y1="0" x2={hoveredPoint.x} y2={chartHeight} stroke="#e5e7eb" />
              <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r="5" fill="#c1122b" stroke="white" strokeWidth="2" />
              <foreignObject x={hoveredPoint.x - 75} y={hoveredPoint.y - 70} width="150" height="60">
                 <div className="bg-white rounded-lg shadow-lg p-2 text-center text-xs border">
                    <p className="font-semibold">{hoveredPoint.day}, {hoveredPoint.date}</p>
                    <p><span className="font-bold">{hoveredPoint.value.toLocaleString()} ألف</span> <span className="text-green-600">+{hoveredPoint.change}% زيارات أعلى</span></p>
                 </div>
              </foreignObject>
            </g>
          )}
        </svg>
        <div className="flex justify-between text-xs text-gray-500 mt-2 px-5">
            {data.map(p => <span key={p.day}>{p.day}</span>)}
        </div>
      </div>
    );
  };

export default function ArticlesPage() {
  const navigate = useNavigate();
  const dir = "rtl";
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [last, setLast] = useState(1);
  
  const [dateRange] = useState({ from: "يناير 2025", to: "يونيو 2025" });

  const donutData = [
    { label: 'المقالات', value: 28100, color: '#f59e0b', percentage: 0.46 },
    { label: 'الاحكام و القرارات', value: 3860, color: '#4f46e5', percentage: 0.32 },
    { label: 'تحديثات القوانين', value: 2040, color: '#14b8a6', percentage: 0.20 },
  ];

  const lineData = [
    { day: 'الجمعة', value: 4000 },
    { day: 'السبت', value: 3000 },
    { day: 'الأحد', value: 5000 },
    { day: 'الإثنين', value: 4500 },
    { day: 'الثلاثاء', value: 6000 },
    { day: 'الأربعاء', value: 2500 },
    { day: 'الخميس', value: 7000, date: '8 يناير', change: 7.1 },
  ];
function getPages(current, last) {
    if (last <= 3) return Array.from({ length: last }, (_, i) => i + 1);
    if (current === 1) return [1, 2, 3];
    if (current === last) return [last - 2, last - 1, last];
    return [current - 1, current, current + 1];
  }
  // Articles state
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadArticles() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchData(`admin/blogs?page=${page}&per_page=${perPage}`);
        // API returns { items, total } or array
        const items = Array.isArray(res) ? res : (res?.items || res?.data || []);
        setArticles(items);
        const totalCount = res?.total || (Array.isArray(res) ? res.length : 0);
        setTotal(totalCount);
        setLast(totalCount && perPage ? Math.max(1, Math.ceil(totalCount / perPage)) : 1);
      } catch (e) {
        setError('فشل في جلب المقالات');
      } finally {
        setLoading(false);
      }
    }
    loadArticles();
  }, [page, perPage]);

  return (
    <div className="min-h-screen p-4 md:p-6 bg-white" dir={dir}>
      <h1 className="text-xl font-bold text-[#1D2035] mb-4">إدارة المقالات</h1>
      
      {/* Charts and Actions */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <LineChart data={lineData} />
          <DonutChart data={donutData} />
        </div>
        <div className="flex items-center gap-2 md:gap-3 mt-6">
            <button 
              onClick={() => navigate('/admin/blog/new')}
              className="px-4 py-2.5 rounded-lg text-white bg-[var(--Primary-Primary)] shadow-sm text-sm md:text-base">
              إضافة مقال جديد
            </button>
            <button className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-[#1D2035] flex items-center gap-2">
              <Calendar size={16} className="text-gray-500" />
              {dateRange.from} - {dateRange.to}
            </button>
            <button className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-[#1D2035] flex items-center gap-2">
              <ArrowUpDown size={16} className="text-gray-500" />
              من الاحدث الى الاقدم
            </button>
        </div>
      </div>

      <Table
        columns={[
          { header: 'العنوان', accessor: 'title', className: 'font-semibold text-[#1D2035]' },
          { header: 'المحتوى', accessor: 'body', className: 'text-gray-700 max-w-[320px] truncate' },
          { header: 'تاريخ النشر', accessor: 'created', className: 'text-gray-700', render: (r) => r.created ? r.created.slice(0, 10) : '' },
        ]}
        rows={articles}
        actions={[]}
            act={[
          {
            label: (
              <div className="flex items-center gap-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.33341 2.66646H4.80008C4.05334 2.66646 3.67998 2.66646 3.39476 2.81179C3.14388 2.93962 2.9399 3.14359 2.81207 3.39448C2.66675 3.67969 2.66675 4.05306 2.66675 4.7998V11.1998C2.66675 11.9465 2.66675 12.3199 2.81207 12.6051C2.9399 12.856 3.14388 13.06 3.39476 13.1878C3.67998 13.3331 4.05334 13.3331 4.80008 13.3331H11.2001C11.9468 13.3331 12.3202 13.3331 12.6054 13.1878C12.8563 13.06 13.0603 12.856 13.1881 12.6051C13.3334 12.3199 13.3334 11.9465 13.3334 11.1998V8.33313M10.3334 3.66646L12.219 5.55208M7.17524 6.82464L11.6074 2.39248C12.1281 1.87178 12.9723 1.87178 13.493 2.39248C14.0137 2.91318 14.0137 3.7574 13.493 4.27809L8.91837 8.85274C8.41061 9.3605 8.15673 9.61438 7.86755 9.81629C7.61087 9.9955 7.33403 10.144 7.04273 10.2586C6.71455 10.3878 6.3626 10.4588 5.6587 10.6008L5.33342 10.6665L5.36505 10.445C5.47699 9.66144 5.53296 9.26965 5.66027 8.90387C5.77325 8.5793 5.92758 8.27063 6.11946 7.98551C6.33569 7.66419 6.61554 7.38434 7.17524 6.82464Z"
                    stroke="#1A6AB3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>{" "}
                <p className="text-[#1A6AB3]">تعديل</p>
              </div>
            ),
            onClick: (row) => navigate(`/admin/blog/${row.uuid}`),
          },
          {
            label: (
              <div className="flex items-center gap-2">
                <p className="text-[#15936E]">تعديل الحالة</p>
              </div>
            ),
            // onClick: (row) => handleToggleStatus(row),
          },
          {
            label: (
              <div className="flex items-center gap-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14.6667 3.33325H11.3334L10.6667 2.66659H5.33341L4.66675 3.33325H1.33341M12.6667 3.33325V12.6666C12.6667 13.1199 12.5261 13.5559 12.2192 13.8628C11.9123 14.1697 11.4763 14.3103 11.0231 14.3103H4.97608C4.52285 14.3103 4.08686 14.1697 3.77996 13.8628C
3.47307 13.5559 3.33241 13.1199 3.33241 12.6666V3.33325H12.6667Z"
                    stroke="#E02424"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M6.66675 7.33325V11.3333"
                    stroke="#E02424"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M9.33325 7.33325V11.3333"
                    stroke="#E02424"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <p className="text-[#E02424]">حذف القانون</p>
              </div>
            ),

            // onClick: (row) => handleDeleteRow(row),
          },
        ]}
        pagination={{
          label: `الصفحة ${page} من ${last}`,
          pages: getPages(page, last),
          current: page,
          last,
          perPage,
          onPageChange: (n) => {
            if (n >= 1 && n <= last) setPage(n);
          },
          onPerPageChange: (n) => {
            setPerPage(n);
            setPage(1);
          },
        }}
      />
    </div>
  );
}
